// Search API endpoints - searches across all transaction types

import apiClient from './client';

export const searchAPI = {
  /**
   * Search across all transaction types
   * @param {Object} filters - Search filters
   * @returns {Promise} Combined search results from all types
   */
  searchAll: async (filters) => {
    const ALL_TYPES = [
      { type: 'cash_sale', typeName: 'Cash Sale', search: searchAPI.searchCashSales, group: 'cashRefund', numberField: 'cashSaleNumber' },
      { type: 'refund', typeName: 'Refund', search: searchAPI.searchRefunds, group: 'cashRefund', numberField: 'refundNumber' },
      { type: 'invoice', typeName: 'Invoice', search: searchAPI.searchInvoices, group: 'accountBased', numberField: 'invoiceNumber' },
      { type: 'credit_note', typeName: 'Credit Note', search: searchAPI.searchCreditNotes, group: 'accountBased', numberField: 'creditNoteNumber' }
    ];

    // A transaction number uniquely identifies a single record, so if any of
    // those are set, only query that one type - everything else is
    // irrelevant to the result.
    const requestedByNumber = ALL_TYPES.filter(t => filters[t.numberField]);

    let typesToSearch;
    if (requestedByNumber.length > 0) {
      typesToSearch = requestedByNumber;
    } else {
      // Customer Name (Cash) only applies to cash sales/refunds; Customer
      // Name (Account) only applies to invoices/credit notes. Without this,
      // searching by Account Name alone still queried cash sales/refunds
      // with no filter that applies to them at all, which silently returned
      // every cash sale/refund in the system instead of none - looking like
      // "search by one field only searched by one field" or "account search
      // also returns cash sales".
      const cashRefundFilterSet = Boolean(filters.customerNameCash);
      const accountFilterSet = Boolean(filters.customerNameAccount);

      typesToSearch = ALL_TYPES.filter(t => {
        if (t.group === 'cashRefund' && accountFilterSet && !cashRefundFilterSet) return false;
        if (t.group === 'accountBased' && cashRefundFilterSet && !accountFilterSet) return false;
        return true;
      });
    }

    const resultsByType = await Promise.all(
      typesToSearch.map(t => t.search(filters))
    );

    // Combine and normalize results
    const allResults = typesToSearch.flatMap((t, i) =>
      resultsByType[i].map(item => ({ ...item, type: t.type, typeName: t.typeName }))
    );

    // Sort by timestamp descending (most recent first)
    return allResults.sort((a, b) =>
      new Date(b.timestamp) - new Date(a.timestamp)
    );
  },

  /**
   * Search cash sales
   * @param {Object} filters - Search filters
   * @returns {Promise} Array of matching cash sales
   */
  searchCashSales: async (filters) => {
    try {
      const response = await apiClient.get('/cashsales', {
        params: buildSearchParams(filters, 'cash_sale')
      });
      return response.data || [];
    } catch (error) {
      console.error('Error searching cash sales:', error);
      return [];
    }
  },

  /**
   * Search invoices
   * @param {Object} filters - Search filters
   * @returns {Promise} Array of matching invoices
   */
  searchInvoices: async (filters) => {
    try {
      const response = await apiClient.get('/invoices', {
        params: buildSearchParams(filters, 'invoice')
      });
      return response.data || [];
    } catch (error) {
      console.error('Error searching invoices:', error);
      return [];
    }
  },

  /**
   * Search credit notes
   * @param {Object} filters - Search filters
   * @returns {Promise} Array of matching credit notes
   */
  searchCreditNotes: async (filters) => {
    try {
      const response = await apiClient.get('/creditnotes', {
        params: buildSearchParams(filters, 'credit_note')
      });
      return response.data || [];
    } catch (error) {
      console.error('Error searching credit notes:', error);
      return [];
    }
  },

  /**
   * Search refunds
   * @param {Object} filters - Search filters
   * @returns {Promise} Array of matching refunds
   */
  searchRefunds: async (filters) => {
    try {
      const response = await apiClient.get('/refunds', {
        params: buildSearchParams(filters, 'refund')
      });
      return response.data || [];
    } catch (error) {
      console.error('Error searching refunds:', error);
      return [];
    }
  }
};

/**
 * Build search params for API call
 * @param {Object} filters - Raw filter values from form
 * @param {string} type - Transaction type
 * @returns {Object} Query parameters for API
 */
function buildSearchParams(filters, type) {
  const params = {};

  // Only include relevant filters for this transaction type
  if (type === 'cash_sale' && filters.cashSaleNumber) {
    params.id = filters.cashSaleNumber;
  }
  if (type === 'invoice' && filters.invoiceNumber) {
    params.id = filters.invoiceNumber;
  }
  if (type === 'credit_note' && filters.creditNoteNumber) {
    params.id = filters.creditNoteNumber;
  }
  if (type === 'refund' && filters.refundNumber) {
    params.id = filters.refundNumber;
  }

  // Common filters
  if (filters.registration) {
    params.registration = filters.registration;
  }
  if (filters.partNumber) {
    params.part_number = filters.partNumber;
  }
  if (filters.itemDescription) {
    params.description = filters.itemDescription;
  }

  // Cash sales/refunds are linked to a Customer; invoices/credit notes are
  // linked to an Account. SearchFilters keeps these as two separate fields
  // since they search different tables.
  const isCashOrRefund = type === 'cash_sale' || type === 'refund';
  if (isCashOrRefund && filters.customerNameCash) {
    params.customer = filters.customerNameCash;
  }
  if (!isCashOrRefund && filters.customerNameAccount) {
    params.account = filters.customerNameAccount;
  }

  if (filters.orderNumber) {
    params.order_number = filters.orderNumber;
  }
  // Backend expects start_timestamp/end_timestamp (ms epoch friendly ISO
  // strings), not start_date/end_date. Expand the end date to the end of
  // that day so a single-day search includes the whole day.
  if (filters.startDate) {
    params.start_timestamp = `${filters.startDate}T00:00:00`;
  }
  if (filters.endDate) {
    params.end_timestamp = `${filters.endDate}T23:59:59`;
  }

  return params;
}
